import React, { useEffect, useMemo } from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import {
  SkeletonGroup,
  SkeletonBone,
  TransactionHistorySkeleton,
} from '@/components/common/skeleton';
import { useResponsive } from '@/hooks/common/useResponsive';
import { StackScreenProps } from '@react-navigation/stack';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { BaseAdminScreen, AdminErrorBoundary } from '@/components';
import { PageHeader, KPISummaryBar } from '@/components/shared/redesign';
import { TransactionsBody } from '@/components/admin/resident/common/TransactionsBody';
import { useResidentDetails } from '@/hooks/admin/useResidentDetails';
import { useLazyGetResidentTransactionInfoQuery } from '@/store/api';

type Props = StackScreenProps<AdminStackParamList, 'TransactionHistory'>;

export const TransactionHistoryScreen: React.FC<Props> = ({ navigation, route }) => {
  const { accountId, leaseId, residentName } = route.params;
  const { showFullTable } = useResponsive();
  const { residentDetails } = useResidentDetails({ accountId, leaseId, mode: 'resident' });
  const [getTx, txState] = useLazyGetResidentTransactionInfoQuery();

  useEffect(() => {
    getTx({ accountId, leaseId });
  }, [accountId, leaseId, getTx]);

  const transactions = (txState.data as any)?.transactions ?? [];

  const summary = useMemo(() => {
    const totalPaid = transactions
      .filter((t: any) => t.type === 'payment')
      .reduce((s: number, t: any) => s + Number(t.amount ?? 0), 0);
    const totalCharged = transactions
      .filter((t: any) => t.type === 'charge')
      .reduce((s: number, t: any) => s + Math.abs(Number(t.amount ?? 0)), 0);
    const outstanding = residentDetails?.balanceDue ?? 0;
    const payCount = transactions.filter((t: any) => t.type === 'payment').length;
    const avg = payCount > 0 ? totalPaid / payCount : 0;
    return { totalPaid, totalCharged, outstanding, avg, count: transactions.length, payCount };
  }, [transactions, residentDetails]);

  const tiles = [
    {
      id: 'charged',
      label: 'Total Charged',
      value: `$${summary.totalCharged.toLocaleString()}`,
    },
    {
      id: 'paid',
      label: 'Total Paid',
      value: `$${summary.totalPaid.toLocaleString()}`,
      trend:
        summary.payCount > 0
          ? { tone: 'success' as const, text: `${summary.payCount} payments` }
          : undefined,
    },
    {
      id: 'outstanding',
      label: 'Outstanding',
      value: `$${summary.outstanding.toLocaleString()}`,
      trend:
        summary.outstanding > 0
          ? { tone: 'warn' as const, text: 'Overdue' }
          : undefined,
    },
    { id: 'avg', label: 'Avg Payment', value: `$${summary.avg.toFixed(0)}` },
  ];

  return (
    <AdminErrorBoundary>
      <BaseAdminScreen
        screenName="TransactionHistory"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
      >
        <ScrollView style={{ flex: 1, padding: 16 }}>
          <PageHeader
            title="Transaction History"
            subtitle={[
              residentName,
              residentDetails?.propertyName,
              residentDetails?.buildingUnitNumberSpace,
              txState.isFetching ? null : `${summary.count} transactions`,
            ]
              .filter(Boolean)
              .join(' · ')}
          />
          {txState.isFetching ? <KPIBarSkeleton /> : <KPISummaryBar tiles={tiles as any} />}
          <View style={{ height: 12 }} />
          {txState.isFetching ? (
            showFullTable ? (
              <TransactionHistorySkeleton variant="table" count={6} showHeader />
            ) : (
              <TransactionHistorySkeleton variant="card" count={6} />
            )
          ) : (
            <TransactionsBody transactions={transactions} />
          )}
        </ScrollView>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const KPI_TILE_COUNT = 4;

const KPIBarSkeleton: React.FC = () => (
  <SkeletonGroup style={kpiSkeletonStyles.row}>
    {Array.from({ length: KPI_TILE_COUNT }).map((_, i) => (
      <View key={i} style={kpiSkeletonStyles.tile}>
        <SkeletonBone width={70} height={11} />
        <SkeletonBone width={90} height={22} />
        <SkeletonBone width={80} height={12} />
      </View>
    ))}
  </SkeletonGroup>
);

const kpiSkeletonStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 8,
  },
  tile: {
    flex: 1,
    gap: 6,
  },
});
